import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  IdTokenResult,
  ParsedToken,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';

// Define UserRole enum to match frontend
enum UserRole {
  FLEET_MANAGER = 'fleetManager',
  TRADER = 'trader',
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminAccessDenied, setAdminAccessDenied] = useState(false);
  const [isAdminFlag, setIsAdminFlag] = useState(false);
  // Ref to always have latest user in popstate handler
  const currentUserRef = useRef<User | null>(null);

  const handleInvalidUser = useCallback(() => {
    setUser(null);
    setIsAdminFlag(false);
    setAdminAccessDenied(true);
  }, []);

  const clearAdminAccessDenied = useCallback(() => {
    setAdminAccessDenied(false);
  }, []);

  // Check if user has admin permissions
  const checkIfUserIsAdmin = useCallback(async (currentUser: User): Promise<boolean> => {
    try {
      const idTokenResult = await currentUser.getIdTokenResult();

      if (!idTokenResult.claims.admin) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking admin permissions:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const isUserAdmin = await checkIfUserIsAdmin(firebaseUser);

          if (!isUserAdmin) {
            handleInvalidUser();
            setLoading(false);
            return;
          }

          // User is admin, set them in state
          currentUserRef.current = firebaseUser;
          setUser(firebaseUser);
          setIsAdminFlag(true);
          setAdminAccessDenied(false);

          // Determine role based on claims
          const idTokenResult = await firebaseUser.getIdTokenResult();
          if (idTokenResult.claims.fleetPermissions) {
            setRole(UserRole.FLEET_MANAGER);
          } else if (idTokenResult.claims.traderPermissions) {
            setRole(UserRole.TRADER);
          } else {
            setRole(null);
          }
        } catch (error) {
          console.error('Error processing authenticated user:', error);
          handleInvalidUser();
        }
      } else {
        currentUserRef.current = null;
        setUser(null);
        setRole(null);
        setIsAdminFlag(false);
        setAdminAccessDenied(false);
      }

      setLoading(false);
    });

    // Handle browser navigation (back/forward buttons)
    const handlePopState = async () => {
      if (currentUserRef.current) {
        try {
          await currentUserRef.current.getIdToken(true); // Force refresh
        } catch (error) {
          console.error('Error refreshing token on popstate:', error);
          setUser(null);
          setIsAdminFlag(false);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      unsubscribe();
      window.removeEventListener('popstate', handlePopState);
    };
  }, [checkIfUserIsAdmin, handleInvalidUser]);

  const signInHandler = useCallback(async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will handle setting user state and admin check
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }, []);

  const signOutHandler = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
      setIsAdminFlag(false);
      setAdminAccessDenied(false);
      currentUserRef.current = null;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  const signUpHandler = useCallback(async (email: string, password: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will handle setting user state and admin check
      return result.user;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }, []);

  const getUser = useCallback(() => {
    return user;
  }, [user]);

  const isAdmin = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    return await checkIfUserIsAdmin(user);
  }, [user, checkIfUserIsAdmin]);

  const getIdTokenResult = useCallback(async (): Promise<IdTokenResult | undefined> => {
    if (!user) return undefined;
    try {
      return await user.getIdTokenResult();
    } catch (error) {
      console.error('Error getting ID token result:', error);
      return undefined;
    }
  }, [user]);

  const getClaims = useCallback(async (): Promise<ParsedToken | undefined> => {
    const tokenResult = await getIdTokenResult();
    return tokenResult?.claims;
  }, [getIdTokenResult]);

  const value = useMemo(
    () => ({
      user,
      currentUser: user,
      role,
      loading,
      signIn: signInHandler,
      signOut: signOutHandler,
      signUp: signUpHandler,
      getUser,
      isAdmin,
      getIdTokenResult,
      getClaims,
      adminAccessDenied,
      clearAdminAccessDenied,
      isAdminFlag,
    }),
    [
      user,
      role,
      loading,
      signInHandler,
      signOutHandler,
      signUpHandler,
      getUser,
      isAdmin,
      getIdTokenResult,
      getClaims,
      adminAccessDenied,
      clearAdminAccessDenied,
      isAdminFlag,
    ]
  );

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
